import React, { useEffect } from 'react';
import { useGlobalContext } from '@/globalContext';
import { Session } from '@models/account';
import { CollectorField, dataCollectorAvailable } from '@models/env';
import { FCMPayload } from '@models/faceCaptureModule';
import { HeaderTab } from '@components/commons';
import { Screen as ConsentScreen } from '@components/consent';
import Loading from '@components/loading';
import { ConfigProps } from '@components/config/ConfigPanel';
import { ScanTabs } from './models';
import styles from './scan.module.scss';
import { Collector, Guide, ScanTab, Settings } from './tabs';

interface Props {
    onSuccess: (res: FCMPayload, base64ImagePreview?: string) => void;
    onError: (error: Error) => void;
    configuration: ConfigProps;
    setConfiguration: (config: ConfigProps) => void;
}

const getScanSelectedTab = (
    scanFirstLanding?: boolean,
    account?: Session,
    collectorFields?: CollectorField[],
): ScanTabs => {
    if (scanFirstLanding) {
        return ScanTabs.Guide;
    }
    if (dataCollectorAvailable(account?.role, collectorFields)) {
        return ScanTabs.DataCollectorSettings;
    }
    return ScanTabs.Scan;
};

const Scan: React.FC<Props> = ({ onSuccess, onError, setConfiguration, configuration }) => {
    const globalContext = useGlobalContext();
    const {
        scanSelectedTab: selectedTab,
        set,
        firstLandingConfig,
        account,
        scanConfiguration: { selectedLanguage },
        envConfig,
    } = globalContext;

    const isCollector = dataCollectorAvailable(account?.role, envConfig?.collectorFields);
    const homeTabs = isCollector
        ? Object.values(ScanTabs)
        : Object.values(ScanTabs).filter((v) => v !== ScanTabs.DataCollectorSettings);

    const setTab = (tab: string) => {
        switch (tab) {
            case ScanTabs.Settings:
            case ScanTabs.Scan:
            case ScanTabs.Guide:
            case ScanTabs.DataCollectorSettings:
                set((previous) => ({
                    ...previous,
                    scanSelectedTab: tab,
                }));
                break;
            default:
                set((previous) => ({
                    ...previous,
                    scanSelectedTab: ScanTabs.Scan,
                }));
                console.log(new Error('Selected tab not available, redirected to Scan'));
                break;
        }
    };

    useEffect(() => {
        if (!selectedTab) {
            set((previous) => ({
                ...previous,
                scanSelectedTab: getScanSelectedTab(
                    previous.firstLandingConfig.scanFirstLanding,
                    account,
                    envConfig?.collectorFields,
                ),
            }));
        }
    }, [set, account, selectedTab, envConfig?.collectorFields]);

    return (
        <ConsentScreen data-qa="scan-consent-screen">
            {selectedTab === undefined ? (
                <Loading />
            ) : (
                <div className={styles['scan']}>
                    <HeaderTab tabs={homeTabs} selected={selectedTab} onClick={setTab} />
                    <div className={styles['scan__wrapper']}>
                        <div className={styles['scan__wrapper__panel']}>
                            {selectedTab === ScanTabs.Settings ? (
                                <Settings
                                    configuration={configuration}
                                    onConfigChange={setConfiguration}
                                    onError={onError}
                                />
                            ) : homeTabs.includes(ScanTabs.DataCollectorSettings) &&
                              selectedTab === ScanTabs.DataCollectorSettings ? (
                                <Collector
                                    configuration={configuration}
                                    onConfigChange={setConfiguration}
                                    onError={onError}
                                />
                            ) : selectedTab === ScanTabs.Guide ? (
                                <Guide
                                    firstLandingConfig={firstLandingConfig}
                                    isCollector={isCollector}
                                    set={set}
                                />
                            ) : (
                                <ScanTab
                                    onError={onError}
                                    onSuccess={onSuccess}
                                    language={selectedLanguage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </ConsentScreen>
    );
};

export default Scan;
